/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { parseMarkdown } from '../utils/markdown';
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { DomController } from './DomController';
import type { NoteService } from './NoteService';
import type { UIService } from './UIService';
import type { AudioService } from './AudioService';

/**
 * Manages all aspects of the rich text editor, including history, formatting, and content sync.
 */
export class EditorService {
  private statusManager: StatusManager;
  
  // Service registry getters
  private get dom() { return ServiceRegistry.get<DomController>('dom'); }
  private get noteService() { return ServiceRegistry.get<NoteService>('noteService'); }
  private get uiService() { return ServiceRegistry.get<UIService>('uiService'); }
  private get audioService() { return ServiceRegistry.get<AudioService>('audioService'); }

  private history: { [key: string]: { stack: string[]; index: number; } } = {};
  private activeEditorId: string | null = null;
  private debounceTimer: number | null = null;
  private lastSelection: Range | null = null;

  // Find & Replace state
  private findRange: Range | null = null;
  private lastFindTerm: string = '';

  constructor() {
    this.statusManager = new StatusManager(this.dom);
    this.bindEventListeners();
    this.bindToolbarListeners();
    this.initHistory();
  }

  private initHistory(): void {
    this.resetHistory('polishedNote');
    this.resetHistory('translatedNote');
  }
  
  public resetHistory(editorId: string): void {
      const editor = document.getElementById(editorId) as HTMLElement;
      if (!editor) return;

      const placeholder = editor.getAttribute('placeholder') || '';
      
      this.history[editorId] = {
          stack: [placeholder],
          index: 0,
      };
      this.updateUndoRedoButtons();
  }
  
  public saveState(immediate = false): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    const performSave = () => {
        if (!this.activeEditorId) return;

        const editor = document.getElementById(this.activeEditorId);
        if (!editor) return;

        const currentContent = editor.innerHTML;
        const historyData = this.history[this.activeEditorId];

        if (currentContent === historyData.stack[historyData.index]) {
            return; // No change
        }

        if (historyData.index < historyData.stack.length - 1) {
            historyData.stack = historyData.stack.slice(0, historyData.index + 1);
        }

        historyData.stack.push(currentContent);
        historyData.index++;
        this.updateUndoRedoButtons();
        this.syncActiveContentToModel();
        // Perform a background save to the local DB for data integrity,
        // but keep the note marked as "dirty" because the user hasn't
        // explicitly clicked the main save button yet.
        this.noteService.saveCurrentNote(false);
    };

    if (immediate) {
        performSave();
    } else {
        this.debounceTimer = window.setTimeout(performSave, 500);
    }
  }

  public undo(): void {
    if (!this.activeEditorId) return;
    const historyData = this.history[this.activeEditorId];
    if (historyData.index > 0) {
        historyData.index--;
        const editor = document.getElementById(this.activeEditorId);
        if (editor) editor.innerHTML = historyData.stack[historyData.index];
        this.updateUndoRedoButtons();
        this.syncActiveContentToModel();
        this.noteService.setDirty(true);
        this.noteService.saveCurrentNote(false);
    }
  }

  public redo(): void {
    if (!this.activeEditorId) return;
    const historyData = this.history[this.activeEditorId];
    if (historyData.index < historyData.stack.length - 1) {
        historyData.index++;
        const editor = document.getElementById(this.activeEditorId);
        if (editor) editor.innerHTML = historyData.stack[historyData.index];
        this.updateUndoRedoButtons();
        this.syncActiveContentToModel();
        this.noteService.setDirty(true);
        this.noteService.saveCurrentNote(false);
    }
  }
  
  private updateUndoRedoButtons(): void {
    if (!this.activeEditorId || this.dom.markdownToolbar.classList.contains('hidden')) {
        this.dom.undoButton.disabled = true;
        this.dom.redoButton.disabled = true;
        return;
    }
    const historyData = this.history[this.activeEditorId];
    if (!historyData) return;
    
    this.dom.undoButton.disabled = historyData.index <= 0;
    this.dom.redoButton.disabled = historyData.index >= historyData.stack.length - 1;
  }
  
  public updateActiveEditorForHistory(): void {
    const activeEditor = this.getActiveEditor();
    if (activeEditor && (activeEditor.id === 'polishedNote' || activeEditor.id === 'translatedNote')) {
        this.activeEditorId = activeEditor.id;
    } else {
        this.activeEditorId = null;
    }
    this.updateUndoRedoButtons();
  }

  public storeSelection(): void {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
        const activeEditor = this.getActiveEditor();
        const range = selection.getRangeAt(0);
        if (activeEditor && activeEditor.contains(range.commonAncestorContainer)) {
            this.lastSelection = range;
        }
    }
  }
  
  private bindEventListeners(): void {
    const editableElements = [
        this.dom.editorTitle,
        this.dom.rawTranscription,
        this.dom.polishedNote,
        this.dom.translatedNote,
    ];
    editableElements.forEach(el => {
        el.addEventListener('paste', (e) => this.handlePaste(e as ClipboardEvent));
        el.addEventListener('input', () => {
            this.noteService.setDirty(true);
            if (el.id === 'polishedNote' || el.id === 'translatedNote') {
                this.saveState();
            } else {
                this.noteService.syncUItoModel(false);
                this.noteService.saveCurrentNote(false);
            }
        });
        el.addEventListener('focus', () => {
            if (el.id === 'polishedNote' || el.id === 'translatedNote') {
                this.activeEditorId = el.id;
                this.updateUndoRedoButtons();
            }
        });
    });

    document.addEventListener('selectionchange', () => {
        this.storeSelection();
    });

    this.dom.findInput.addEventListener('input', () => {
        this.lastFindTerm = ''; // Reset on new input
        this.findRange = null;
    });
  }
  
  private async handlePaste(event: ClipboardEvent): Promise<void> {
    event.preventDefault();
    const target = event.target as HTMLElement;
    if (!target || !event.clipboardData) return;

    const pastedHtml = event.clipboardData.getData('text/html');
    const pastedText = event.clipboardData.getData('text/plain');

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    let fragment: DocumentFragment;
    const isMarkdownEditor = target.id === 'polishedNote' || target.id === 'translatedNote';
    const tableMarkdown = isMarkdownEditor ? this.tryBuildMarkdownTableFromClipboard(pastedText, pastedHtml) : null;

    if (target.id === 'editorTitle' || target.id === 'rawTranscription') {
        fragment = range.createContextualFragment(pastedText);
    } else if (tableMarkdown) {
        if (this.dom.getSourceViewState()) {
            fragment = document.createDocumentFragment();
            fragment.appendChild(document.createTextNode(tableMarkdown));
        } else {
            const tableHtml = await parseMarkdown(tableMarkdown);
            fragment = range.createContextualFragment(tableHtml);
        }
    } else if (pastedHtml) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = pastedHtml;

        const unwrap = (el: Element) => {
            const parent = el.parentNode;
            if (parent) {
                while (el.firstChild) {
                    parent.insertBefore(el.firstChild, el);
                }
                parent.removeChild(el);
            }
        };

        tempDiv.querySelectorAll('style, script, meta, link').forEach(el => el.remove());
        
        tempDiv.querySelectorAll('*').forEach(el => {
            const htmlEl = el as HTMLElement;

            if ((htmlEl.tagName === 'B' || htmlEl.tagName === 'STRONG') && htmlEl.style.fontWeight === 'normal') {
                unwrap(htmlEl);
                return;
            }
            if ((htmlEl.tagName === 'I' || htmlEl.tagName === 'EM') && htmlEl.style.fontStyle === 'normal') {
                unwrap(htmlEl);
                return;
            }
            if (htmlEl.tagName === 'FONT') {
                unwrap(htmlEl);
                return;
            }

            htmlEl.removeAttribute('style');
            htmlEl.removeAttribute('class');
            htmlEl.removeAttribute('id');

            if (htmlEl.tagName === 'SPAN' && htmlEl.attributes.length === 0) {
                unwrap(htmlEl);
            }
        });

        fragment = range.createContextualFragment(tempDiv.innerHTML);

    } else if (pastedText) {
        const generatedHtml = await parseMarkdown(pastedText, { breaks: true });
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = generatedHtml.trim();
        const contentToInsert = (tempDiv.children.length === 1 && tempDiv.firstElementChild?.tagName === 'P')
            ? tempDiv.firstElementChild.innerHTML
            : tempDiv.innerHTML;
        fragment = range.createContextualFragment(contentToInsert);
    } else {
        fragment = document.createDocumentFragment();
    }

    const lastNode = fragment.lastChild;
    range.insertNode(fragment);

    if (lastNode) {
        range.setStartAfter(lastNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    target.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
}

  private tryBuildMarkdownTableFromClipboard(pastedText: string, pastedHtml: string): string | null {
    const htmlTableRows = this.extractRowsFromHtmlTable(pastedHtml);
    if (htmlTableRows && htmlTableRows.length > 0) {
      return this.rowsToMarkdownTable(htmlTableRows);
    }

    const textRows = this.extractRowsFromTabularText(pastedText);
    if (textRows && textRows.length > 0) {
      return this.rowsToMarkdownTable(textRows);
    }

    return null;
  }

  private extractRowsFromHtmlTable(pastedHtml: string): string[][] | null {
    if (!pastedHtml || !/<table[\s>]/i.test(pastedHtml)) {
      return null;
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = pastedHtml;
    const table = wrapper.querySelector('table');
    if (!table) return null;

    const rows = Array.from(table.querySelectorAll('tr'))
      .map(row => Array.from(row.querySelectorAll('th, td')).map(cell => this.normalizeTableCell(cell.textContent || '')))
      .filter(row => row.length > 1);

    return rows.length > 0 ? rows : null;
  }

  private extractRowsFromTabularText(pastedText: string): string[][] | null {
    if (!pastedText || !pastedText.includes('\t')) {
      return null;
    }

    const rows = pastedText
      .replace(/\r\n?/g, '\n')
      .trim()
      .split('\n')
      .map(row => row.split('\t').map(cell => this.normalizeTableCell(cell)))
      .filter(row => row.length > 1);

    return rows.length > 0 ? rows : null;
  }

  private rowsToMarkdownTable(rows: string[][]): string {
    const maxColumns = Math.max(...rows.map(row => row.length));
    const normalizedRows = rows.map(row => {
      const padded = [...row];
      while (padded.length < maxColumns) {
        padded.push('');
      }
      return padded;
    });

    const escapeCell = (value: string) => value.replace(/\|/g, '\\|');
    const header = `| ${normalizedRows[0].map(escapeCell).join(' | ')} |`;
    const separator = `| ${Array.from({ length: maxColumns }, () => '---').join(' | ')} |`;
    const body = normalizedRows.slice(1).map(row => `| ${row.map(escapeCell).join(' | ')} |`);

    return [header, separator, ...body].join('\n');
  }

  private normalizeTableCell(value: string): string {
    return value
      .replace(/\r\n?/g, '\n')
      .replace(/\u00a0/g, ' ')
      .replace(/\s*\n\s*/g, '<br/>')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private bindToolbarListeners(): void {
    const format = (command: string, value: string | null = null) => {
        this.noteService.setDirty(true);
        document.execCommand(command, false, value);
        this.getActiveEditor()?.focus();
        this.saveState(true);
    };

    const handleMousedown = (e: MouseEvent) => e.preventDefault();
    
    const formatBlock = (tag: string) => {
      this.noteService.setDirty(true);
      document.execCommand('formatBlock', false, `<${tag}>`);
      this.getActiveEditor()?.focus();
      this.saveState(true);
    }

    this.dom.paragraphButton.addEventListener('mousedown', handleMousedown);
    this.dom.paragraphButton.addEventListener('click', () => formatBlock('p'));
    this.dom.h1Button.addEventListener('mousedown', handleMousedown);
    this.dom.h1Button.addEventListener('click', () => formatBlock('h1'));
    this.dom.h2Button.addEventListener('mousedown', handleMousedown);
    this.dom.h2Button.addEventListener('click', () => formatBlock('h2'));
    this.dom.h3Button.addEventListener('mousedown', handleMousedown);
    this.dom.h3Button.addEventListener('click', () => formatBlock('h3'));
    this.dom.h4Button.addEventListener('mousedown', handleMousedown);
    this.dom.h4Button.addEventListener('click', () => formatBlock('h4'));
    this.dom.boldButton.addEventListener('mousedown', handleMousedown);
    this.dom.boldButton.addEventListener('click', () => format('bold'));
    this.dom.italicButton.addEventListener('mousedown', handleMousedown);
    this.dom.italicButton.addEventListener('click', () => format('italic'));
    this.dom.strikethroughButton.addEventListener('mousedown', handleMousedown);
    this.dom.strikethroughButton.addEventListener('click', () => format('strikeThrough'));
    this.dom.ulButton.addEventListener('mousedown', handleMousedown);
    this.dom.ulButton.addEventListener('click', () => format('insertUnorderedList'));
    this.dom.olButton.addEventListener('mousedown', handleMousedown);
    this.dom.olButton.addEventListener('click', () => format('insertOrderedList'));
    this.dom.indentButton.addEventListener('mousedown', handleMousedown);
    this.dom.indentButton.addEventListener('click', () => format('indent'));
    this.dom.outdentButton.addEventListener('mousedown', handleMousedown);
    this.dom.outdentButton.addEventListener('click', () => format('outdent'));
    this.dom.clearFormatButton.addEventListener('mousedown', handleMousedown);
    this.dom.clearFormatButton.addEventListener('click', () => format('removeFormat'));
    this.dom.linkButton.addEventListener('mousedown', handleMousedown);
    this.dom.linkButton.addEventListener('click', () => this.addLinkToSelection());
    this.dom.cleanMarkdownButton.addEventListener('mousedown', handleMousedown);
    this.dom.cleanMarkdownButton.addEventListener('click', () => this.cleanCurrentEditorMarkdown());
    this.dom.codeButton.addEventListener('mousedown', handleMousedown);
    this.dom.codeButton.addEventListener('click', () => this.formatSelectionWithTag('code'));
    this.dom.checklistButton.addEventListener('mousedown', handleMousedown);
    this.dom.checklistButton.addEventListener('click', () => this.insertChecklist());

    this.dom.undoButton.addEventListener('click', () => this.undo());
    this.dom.redoButton.addEventListener('click', () => this.redo());

    // Handle link clicks in contenteditable areas
    this.setupLinkClickHandlers();
  }

  private setupLinkClickHandlers(): void {
    // Handle clicks on links in the note content areas
    const noteContents = [this.dom.polishedNote, this.dom.translatedNote];
    
    noteContents.forEach(editor => {
      if (editor) {
        editor.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;
          if (target.tagName === 'A' && target.classList.contains('markdown-link')) {
            e.preventDefault(); // Prevent default link behavior
            this.handleLinkClick(target as HTMLAnchorElement, e);
          }
        });
      }
    });
  }

  private handleLinkClick(link: HTMLAnchorElement, event: MouseEvent): void {
    // Check for Ctrl+Click or Cmd+Click to open link
    if (event.ctrlKey || event.metaKey) {
      window.open(link.href, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Regular click - also open in new tab for safety
    window.open(link.href, '_blank', 'noopener,noreferrer');
  }

  private formatSelectionWithTag(tagName: 'code'): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    const activeEditor = this.getActiveEditor();
    if (!activeEditor || !activeEditor.contains(selection.anchorNode)) return;

    this.noteService.setDirty(true);
    const wrapper = document.createElement(tagName);
    try {
        wrapper.appendChild(range.extractContents());
        range.insertNode(wrapper);
        selection.removeAllRanges();
        selection.addRange(range);
    } catch(e) {
        ErrorHandler.handle('wrapping selection', e, this.dom, false);
        document.execCommand('insertHTML', false, `<${tagName}>${selection.toString()}</${tagName}>`);
    }
    this.saveState(true);
    activeEditor.focus();
  }
  
  private insertChecklist(): void {
    this.noteService.setDirty(true);
    document.execCommand('insertUnorderedList');
    setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || !selection.anchorNode) return;
        let listContainer: Node | null = selection.anchorNode;
        while(listContainer && listContainer.nodeName !== 'UL' && listContainer.nodeName !== 'OL') {
            listContainer = listContainer.parentNode;
        }

        if (listContainer) {
            const listItems = Array.from((listContainer as HTMLElement).querySelectorAll('li'));
            const firstSelectedItem = listItems.find(li => selection.containsNode(li, true));
            const shouldRemove = firstSelectedItem?.innerHTML.trim().startsWith('[ ]') || firstSelectedItem?.innerHTML.trim().startsWith('[x]');
            listItems.forEach(li => {
                if (selection.containsNode(li, true)) {
                    const currentHTML = li.innerHTML.trim();
                    if (shouldRemove) {
                        li.innerHTML = currentHTML.replace(/^(?:\[[ x]\]\s*)+/, '');
                    } else if (!currentHTML.startsWith('[ ]') && !currentHTML.startsWith('[x]')) {
                        li.innerHTML = `[ ] ${currentHTML}`;
                    }
                }
            });
        }
        this.saveState(true);
    }, 100);
  }

  public getActiveEditor(): HTMLElement | null {
    return document.querySelector('.note-content.active') as HTMLElement | null;
  }

  public syncActiveContentToModel(): void {
    const activeEditor = this.getActiveEditor();
    if (!activeEditor || !this.noteService.currentNote) return;

    if (activeEditor.id === 'polishedNote' || activeEditor.id === 'translatedNote') {
        const markdown = this.convertHtmlToMarkdown(activeEditor.innerHTML);
        if (activeEditor.id === 'polishedNote') this.noteService.currentNote.polishedNote = markdown;
        else this.noteService.currentNote.translatedNote = markdown;
    } else if (activeEditor.id === 'rawTranscription') {
        this.noteService.currentNote.rawTranscription = activeEditor.textContent || '';
    }
  }

  public convertHtmlToMarkdown(html: string): string {
    const tempDiv = document.createElement('div');

    // A more robust conversion process
    // 1. Normalize line breaks from contenteditable (which often uses <div> or <p>)
    let processedHtml = html
      .replace(/&nbsp;/gi, ' ')
      .replace(/<div/gi, '<p')
      .replace(/<\/div>/gi, '</p>')
      .replace(/<p>\s*<br\s*\/?>(\s*)<\/p>/gi, '\n');
    
    tempDiv.innerHTML = processedHtml;

    // 2. Process lists separately to correctly handle their structure
    tempDiv.querySelectorAll('ol, ul').forEach(list => {
        let markdownList = '\n';
        const isOrdered = list.tagName === 'OL';
        Array.from(list.children)
          .filter(child => child.tagName === 'LI')
          .forEach((li, index) => {
            const prefix = isOrdered ? `${index + 1}. ` : '- ';
            const innerHtml = (li.innerHTML || '').trim().replace(/<br\s*\/?>/gi, '\n');
            if (innerHtml.startsWith('[ ]') || innerHtml.startsWith('[x]')) {
                markdownList += `- ${innerHtml}\n`;
            } else {
                markdownList += `${prefix}${innerHtml}\n`;
            }
          });
        list.replaceWith(document.createTextNode(markdownList));
    });

    let md = tempDiv.innerHTML;
    
    // 3. Convert block elements to markdown with appropriate newlines
    md = md.replace(/<br\s*\/?>/gi, '\n');
    md = md.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '\n# $1\n');
    md = md.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '\n## $1\n');
    md = md.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '\n### $1\n');
    md = md.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '\n#### $1\n');
    md = md.replace(/<p[^>]*>(.*?)<\/p>/gi, '\n$1\n');

    // 4. Convert inline elements
    md = md.replace(/<strong[^>]*>|<\/strong>/gi, '**');
    md = md.replace(/<b[^>]*>|<\/b>/gi, '**');
    md = md.replace(/<em[^>]*>|<\/em>/gi, '*');
    md = md.replace(/<i[^>]*>|<\/i>/gi, '*');
    md = md.replace(/<del[^>]*>|<\/del>/gi, '~~');
    md = md.replace(/<s[^>]*>|<\/s>/gi, '~~');
    md = md.replace(/<strike[^>]*>|<\/strike>/gi, '~~');
    md = md.replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`');

    tempDiv.querySelectorAll('table').forEach(table => {
      const rows = Array.from(table.querySelectorAll('tr'))
        .map(row => Array.from(row.querySelectorAll('th, td')).map(cell => this.normalizeTableCell(cell.textContent || '')))
        .filter(row => row.length > 1);

      if (rows.length > 0) {
        const markdownTable = this.rowsToMarkdownTable(rows);
        table.replaceWith(document.createTextNode(`\n${markdownTable}\n`));
      } else {
        table.remove();
      }
    });

    md = tempDiv.innerHTML;
    
    // 5. Clean up by removing any remaining HTML and decoding entities
    const decoder = document.createElement('textarea');
    decoder.innerHTML = md.replace(/<[^>]+>/g, '');
    
    // 6. Normalize newlines and trim whitespace
    return decoder.value
      .replace(/\r\n?/g, '\n')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  public async cleanCurrentEditorMarkdown(): Promise<void> {
    const activeEditor = this.getActiveEditor();
    if (!activeEditor || (activeEditor.id !== 'polishedNote' && activeEditor.id !== 'translatedNote')) {
      this.statusManager.custom('Cleanup is available on Polished and Translated tabs.');
      return;
    }

    const isSourceView = this.dom.getSourceViewState();
    const currentMarkdown = isSourceView
      ? (activeEditor.textContent || '')
      : this.convertHtmlToMarkdown(activeEditor.innerHTML);

    const cleanedMarkdown = this.cleanMarkdownArtifacts(currentMarkdown);
    if (!cleanedMarkdown) {
      this.statusManager.custom('Nothing to clean.');
      return;
    }

    if (isSourceView) {
      activeEditor.textContent = cleanedMarkdown;
    } else {
      activeEditor.innerHTML = await parseMarkdown(cleanedMarkdown);
    }

    if (this.noteService.currentNote) {
      if (activeEditor.id === 'polishedNote') {
        this.noteService.currentNote.polishedNote = cleanedMarkdown;
      } else {
        this.noteService.currentNote.translatedNote = cleanedMarkdown;
      }
    }

    this.noteService.setDirty(true);
    this.noteService.saveCurrentNote(false);
    this.saveState(true);
    this.statusManager.success('Markdown cleaned');
  }

  private cleanMarkdownArtifacts(markdown: string): string {
    let cleaned = markdown
      .replace(/\r\n?/g, '\n')
      .replace(/\u200B/g, '')
      .replace(/<\/?(div|span|font)[^>]*>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/^[ \t]+$/gm, '')
      .replace(/[ \t]+$/gm, '');

    const lines = cleaned.split('\n').map(line => {
      const headingFixed = line.replace(/^(\s{0,3}#{1,6})([^\s#])/,'$1 $2');
      const bulletFixed = headingFixed.replace(/^\s*\*\s+/,'- ');
      return bulletFixed;
    });

    cleaned = lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();

    return cleaned;
  }

  public insertContent(htmlContent: string): void {
      const activeEditor = this.getActiveEditor();
      if (!activeEditor) return;
      
      activeEditor.focus();
      
      const selection = window.getSelection();
      let range: Range | null = this.lastSelection;

      if (!range || (selection && selection.rangeCount > 0 && !activeEditor.contains(range.commonAncestorContainer))) {
        if (selection && selection.rangeCount > 0) {
            range = selection.getRangeAt(0);
        } else {
            range = document.createRange();
            range.selectNodeContents(activeEditor);
            range.collapse(false);
        }
      }

      if (selection && range) {
          selection.removeAllRanges();
          selection.addRange(range);
          
          range.deleteContents();
          const fragment = range.createContextualFragment(htmlContent);
          const lastNode = fragment.lastChild;
          range.insertNode(fragment);
          
          if (lastNode) {
            range.setStartAfter(lastNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
      }
      
      this.saveState(true);
  }

  public addLinkToSelection(): void {
    const activeEditor = this.getActiveEditor();
    if (!activeEditor || (activeEditor.id !== 'polishedNote' && activeEditor.id !== 'translatedNote')) {
      this.statusManager.custom('Links can be added to Polished and Translated tabs only.');
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      this.statusManager.custom('Please select text to add a link.');
      return;
    }

    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    if (!selectedText) {
      this.statusManager.custom('Please select text to add a link.');
      return;
    }

    // Show input dialog for URL
    const url = prompt('Enter URL:', 'https://');
    if (!url || url.trim() === '' || url === 'https://') {
      return; // User cancelled or entered empty URL
    }

    const isSourceView = this.dom.getSourceViewState();
    
    if (isSourceView) {
      // In source view, insert markdown link format
      const markdownLink = `[${selectedText}](${url.trim()})`;
      range.deleteContents();
      const textNode = document.createTextNode(markdownLink);
      range.insertNode(textNode);
    } else {
      // In WYSIWYG view, insert HTML link
      const linkElement = document.createElement('a');
      linkElement.href = url.trim();
      linkElement.textContent = selectedText;
      linkElement.target = '_blank'; // Open links in new tab
      linkElement.rel = 'noopener noreferrer'; // Security best practice
      linkElement.className = 'markdown-link'; // Add CSS class for styling
      
      range.deleteContents();
      range.insertNode(linkElement);
    }

    // Update selection and note content
    selection.removeAllRanges();
    selection.addRange(range);
    
    this.noteService.setDirty(true);
    this.updateNoteContentFromEditor();
    this.saveState(true);
    this.statusManager.success('Link added');
  }

  private updateNoteContentFromEditor(): void {
    if (!this.noteService.currentNote) return;
    
    const activeEditor = this.getActiveEditor();
    if (!activeEditor) return;

    const isSourceView = this.dom.getSourceViewState();
    
    if (activeEditor.id === 'polishedNote') {
      this.noteService.currentNote.polishedNote = isSourceView 
        ? (activeEditor.textContent || '')
        : this.convertHtmlToMarkdown(activeEditor.innerHTML);
    } else if (activeEditor.id === 'translatedNote') {
      this.noteService.currentNote.translatedNote = isSourceView 
        ? (activeEditor.textContent || '')
        : this.convertHtmlToMarkdown(activeEditor.innerHTML);
    }
  }

  public findAndReplace(action: 'next' | 'prev' | 'replace' | 'replaceAll'): void {
    const editor = this.getActiveEditor();
    if (!editor) return;

    const findTerm = this.dom.findInput.value;
    const replaceTerm = this.dom.replaceInput.value;

    if (!findTerm && action !== 'replace') return;

    if (action === 'replaceAll') {
        const originalContent = editor.innerHTML;
        const newContent = originalContent.replace(new RegExp(findTerm, 'gi'), replaceTerm);
        if (originalContent !== newContent) {
            editor.innerHTML = newContent;
            this.saveState(true);
        }
        return;
    }
    
    const selection = window.getSelection()!;
    if (this.lastFindTerm !== findTerm) {
        this.findRange = null;
        this.lastFindTerm = findTerm;
    }

    if (action === 'replace') {
        if (selection.rangeCount > 0 && selection.toString().toLowerCase() === findTerm.toLowerCase()) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            range.insertNode(document.createTextNode(replaceTerm));
            this.saveState(true);
            action = 'next'; // Automatically find the next one
        }
    }
    
    const backward = action === 'prev';
    if (this.findRange) {
        selection.removeAllRanges();
        selection.addRange(this.findRange);
    }
    
    // The 'find' API is not universally supported and can be tricky with contenteditable.
    // A more robust solution might involve a custom walker, but window.find is a simpler start.
    try {
        // FIX: Cast 'window' to 'any' to access the non-standard 'find' method and resolve the TypeScript error.
        const found = (window as any).find(findTerm, false, backward, true, false, true, false);
        if (found) {
            this.findRange = selection.getRangeAt(0).cloneRange();
            editor.focus(); // Re-focus editor after find
        } else {
            this.findRange = null; // Reset if not found
            this.statusManager.custom('Reached end of document');
        }
    } catch (e) {
        ErrorHandler.handle('finding text', e, this.dom, false);
    }
  }
}